import { useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useAdminMerchants } from "@/api/admin/merchants";
import { useAdminWorkspaces } from "@/api/admin/workspaces";
import { usePagination } from "@/api/pagination";
import { LogoStack, type LogoStackItem } from "@/components/admin/logo-stack";
import { WorkspaceLogo } from "@/components/admin/workspace-logo";
import { MerchantLogo } from "@/components/application/merchant-logo/merchant-logo";
import { PaginationPageMinimalCenter } from "@/components/application/pagination/pagination";
import { Table, TableCard } from "@/components/application/table/table";
import { Badge } from "@/components/base/badges/badges";
import { useCache } from "@/hooks/use-cache";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useSafetyModelCount } from "@/hooks/use-safety-model-count";
import { useSort } from "@/hooks/use-sort";
import { formatDate } from "@/utils/date-conversion";
import { FEATURE_LABELS } from "@/utils/features";

const SafetyModelsCell = ({ merchantId }: { merchantId: string }) => {
  const { total, hasCustom } = useSafetyModelCount(merchantId);

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-primary">{total}</span>
      {hasCustom && <Badge color="gray-blue">Custom</Badge>}
    </div>
  );
};

const featuresColor = {
  api: "blue-light",
  assurance: "purple",
  insights: "orange",
  explorer: "success",
  reviewQueue: "brand",
  veryLowVolume: "blue",
  hideExplorers: "gray",
  noAffiliateAuthorisation: "warning",
  pastDueBanner: "error",
} as const;

type AdminMerchantsTableProps = {
  search: string;
  /** Pass both to keep the current page outside the table (e.g. in the URL). */
  page?: number;
  onPageChange?: (page: number) => void;
};

export const AdminMerchantsTable = ({ search, page, onPageChange }: AdminMerchantsTableProps) => {
  const navigate = useNavigate();
  const pagination = usePagination(undefined, page !== undefined && onPageChange ? { page, onPageChange } : undefined);
  const sort = useSort<"name" | "createdAt">("name", "asc");
  const { data } = useAdminMerchants(pagination.pagination, sort.sort, { text: search });
  const total = useCache(data?.total);
  const { data: workspacesData } = useAdminWorkspaces(
    { offset: 0, limit: 500 },
    { column: "name", direction: "asc" },
    {},
  );

  // Build merchant items with their workspace lists pre-computed so that when
  // workspacesData arrives the items array gets a new reference, forcing
  // react-aria's Table.Body to re-render every row.
  const merchantsWithWorkspaces = useMemo(() => {
    const allWorkspaces = workspacesData?.workspaces ?? [];
    return (data?.merchants ?? []).map((merchant) => ({
      ...merchant,
      workspaceItems: allWorkspaces.filter((ws) => ws.merchants.some((m) => m.id === merchant.id)) as LogoStackItem[],
    }));
  }, [data?.merchants, workspacesData?.workspaces]);

  return (
    <TableCard.Root className="h-full flex flex-col">
      <div className="flex-1 min-h-0 overflow-auto">
        <Table aria-label="Merchants" sortDescriptor={sort.descriptor} onSortChange={sort.setFromDescriptor}>
          <Table.Header>
            <Table.Head id="name" label="Name" isRowHeader allowsSorting className="w-[20%]" />
            <Table.Head id="region" label="Region" className="w-[8%]" />
            <Table.Head id="owner" label="Owner" className="w-[15%]" />
            <Table.Head id="workspaces" label="Workspaces" className="w-[14%]" />
            <Table.Head id="features" label="Permissions" className="w-[15%]" />
            <Table.Head id="ads" label="TikTok Ads" className="w-[7%]" />
            <Table.Head id="safety" label="Safety Models" className="w-[10%]" />
            <Table.Head id="users" label="Users" className="w-[6%]" />
            <Table.Head id="createdAt" label="Created" allowsSorting className="w-[8%]" />
          </Table.Header>

          <Table.Body items={merchantsWithWorkspaces}>
            {(item) => {
              const merchantWorkspaces = item.workspaceItems;

              return (
                <Table.Row id={item.id}>
                  <Table.Cell className="whitespace-nowrap p-0">
                    <button
                      type="button"
                      onClick={() => navigate({ to: `/admin/merchants/${item.id}` })}
                      className="w-full h-full flex items-center gap-3 px-4 py-3"
                    >
                      <MerchantLogo name={item.name} logoUrl={item.logoUrl} size="sm" />
                      <div className="flex flex-col text-left">
                        <p className="text-sm font-medium text-primary">{item.name}</p>
                        <p className="text-sm text-tertiary">ID: {item.id}</p>
                      </div>
                    </button>
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    {item.region ? (
                      <Badge color="gray-blue">{item.region}</Badge>
                    ) : (
                      <span className="text-sm text-tertiary">—</span>
                    )}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    {item.ownerWorkspace ? (
                      <WorkspaceLogo
                        name={item.ownerWorkspace.name}
                        logoUrl={item.ownerWorkspace.logoUrl}
                        className="size-8 rounded"
                      />
                    ) : (
                      <span className="text-sm text-tertiary">—</span>
                    )}
                  </Table.Cell>
                  <Table.Cell className="min-w-[140px]">
                    {merchantWorkspaces.length > 0 ? (
                      <LogoStack items={merchantWorkspaces} />
                    ) : (
                      <span className="text-sm text-tertiary">Not in any workspaces</span>
                    )}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    <div className="flex flex-row flex-wrap gap-1">
                      {item.features.map((feature) => (
                        <Badge color={featuresColor[feature]} key={feature}>
                          {FEATURE_LABELS[feature]}
                        </Badge>
                      ))}
                    </div>
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    {item.hasTikTokAdsAuth ? (
                      <Badge color="success">Connected</Badge>
                    ) : (
                      <span className="text-sm text-tertiary">—</span>
                    )}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    <SafetyModelsCell merchantId={item.id} />
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap text-sm text-primary">{item.users.length}</Table.Cell>
                  <Table.Cell className="whitespace-nowrap">{formatDate(item.createdAt)}</Table.Cell>
                </Table.Row>
              );
            }}
          </Table.Body>
        </Table>
      </div>

      <PaginationPageMinimalCenter
        page={pagination.page}
        total={Math.ceil((total ?? 0) / pagination.pagination.limit)}
        onPageChange={pagination.setPage}
        className="px-4 py-3 md:px-6 md:pt-3 md:pb-4"
      />
    </TableCard.Root>
  );
};
